// Personal details (§13.3): read-only customer record. Edits happen through
// the branch in the mock world, so there is no edit path here.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { getProfile } from '@/api/profile';
import type { RootState } from '@/store';
import type { ProfileInfo } from '@/types/phase2.types';
import { formatLong } from '@/mock/clock';
import Loader from '@/components/ui/Loader/Loader';
import './profile.css';

const KYC_LABELS: Record<ProfileInfo['kycState'], string> = {
  FULL: 'KYC complete',
  LIMITED: 'Limited KYC',
  PENDING: 'KYC pending',
};

const PersonalDetails = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.account.user);
  const selectedAccount = useSelector((state: RootState) => state.account.selectedAccount);
  const [profile, setProfile] = useState<ProfileInfo | null>(null);

  useEffect(() => {
    getProfile({
      name: user?.fullName,
      email: user?.email,
      product: selectedAccount?.accountType,
      applicationRef: user?.customerId,
    })
      .then(setProfile)
      .catch(() => setProfile(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: Array<[string, string]> = profile
    ? [
        ['Name', profile.name],
        ['Email', profile.email],
        ['Mobile', user?.mobileNumber ? `+91 ${user.mobileNumber}` : '—'],
        ['Product', profile.product],
        ['Application reference', profile.applicationRef],
        ['KYC', KYC_LABELS[profile.kycState]],
        [
          'Mobile banking',
          profile.mobileBanking ? 'Set up' : 'Not set up',
        ],
        [
          'Last login',
          profile.firstLogin ? 'This is your first login' : profile.lastLoginAt ? formatLong(profile.lastLoginAt) : '—',
        ],
      ]
    : [];

  return (
    <div className="nova-profile-page">
      <div className="nova-pay-flow-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/profile')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Personal details</h2>
      </div>

      {!profile && <Loader rows={4} />}
      {profile && (
        <dl className="nova-profile-rows nova-receipt-rows">
          {rows.map(([label, value]) => (
            <div className="nova-receipt-row" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

export default PersonalDetails;
